import type { KeyValueStore } from "./types.js";

/**
 * Browser KeyValueStore. Identity (visitor/session ids) lives in first-party cookies so the
 * server webhook can read the same visitor_id off the checkout (the PHP plugin copies our cookie
 * into order meta). Cookies are SameSite=Lax + Secure; non-identity keys (landing/seen) fall back
 * to the same cookie jar for simplicity.
 *
 * localStorage is mirrored on every write and used as a read fallback so that session identity
 * survives page reloads even when the browser clears session cookies (Safari ITP, mobile tabs).
 *
 * A Map-backed store (for tests / SSR) is in `memoryStore()`.
 */
export function cookieStore(doc: Document = document): KeyValueStore {
  const secure = typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";

  function lsGet(key: string): string | null {
    try { return localStorage.getItem(key); } catch { return null; }
  }
  function lsSet(key: string, value: string): void {
    try { localStorage.setItem(key, value); } catch { /* storage blocked */ }
  }

  return {
    get(key: string): string | null {
      const prefix = `${encodeURIComponent(key)}=`;
      for (const part of doc.cookie ? doc.cookie.split("; ") : []) {
        if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
      }
      // Fallback: cookie may have been cleared by browser (ITP, mobile tab restore).
      return lsGet(key);
    },
    set(key: string, value: string, ttlDays?: number): void {
      let cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; Path=/; SameSite=Lax${secure}`;
      if (ttlDays !== undefined) {
        const expires = new Date(Date.now() + ttlDays * 86_400_000).toUTCString();
        cookie += `; Expires=${expires}`;
      }
      doc.cookie = cookie;
      lsSet(key, value);
    },
  };
}

/** In-memory store for unit tests and non-browser contexts. */
export function memoryStore(): KeyValueStore {
  const map = new Map<string, string>();
  return {
    get: (key) => map.get(key) ?? null,
    set: (key, value) => void map.set(key, value),
  };
}
